"use client";

import { useCallback, useState } from "react";

export type StepperNavigation<T> = {
  stepIndex: number;
  setStepIndex: React.Dispatch<React.SetStateAction<number>>;
  current: T;
  total: number;
  goBack: () => void;
  goNext: () => void;
  isLast: boolean;
  submitting: boolean;
  setSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  errorMessage: string | null;
  setErrorMessage: React.Dispatch<React.SetStateAction<string | null>>;
};

/**
 * Encapsulates the stepper navigation and submission state shared by multi-step
 * tool card forms (`AskCardForm`, `AskOpenForm`).
 *
 * Each card keeps its own domain state (selections, texts, validation, submit handler,
 * step body JSX) — only the mechanical navigation and error chrome are shared.
 */
export function useStepperNavigation<T>(items: T[]): StepperNavigation<T> {
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const total = items.length;
  const current = items[stepIndex]!;
  const isLast = stepIndex === total - 1;

  const goBack = useCallback(() => setStepIndex(i => Math.max(0, i - 1)), []);
  const goNext = useCallback(() => setStepIndex(i => Math.min(total - 1, i + 1)), [total]);

  return {
    stepIndex, setStepIndex, current, total, goBack, goNext, isLast,
    submitting, setSubmitting, errorMessage, setErrorMessage,
  };
}
